/**
 * DensityContext — global verfügbarer Tabellen-Density-Switch.
 * Spec: docs/design-spec.md §5.1.
 *
 * Persistiert in `localStorage['paypilot.density']`.
 * Nutzung:
 *   <DensityProvider>{...}</DensityProvider>
 *   const { density, setDensity } = useDensity();
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Density = "compact" | "cozy" | "relaxed";

const STORAGE_KEY = "paypilot.density";
const VALID: readonly Density[] = ["compact", "cozy", "relaxed"] as const;

interface DensityContextValue {
  density: Density;
  setDensity: (d: Density) => void;
}

const DensityContext = createContext<DensityContextValue | undefined>(undefined);

function readInitial(defaultDensity: Density): Density {
  if (typeof window === "undefined") return defaultDensity;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && VALID.includes(raw as Density)) return raw as Density;
  } catch {
    // ignore (Privacy-Mode/Quota)
  }
  return defaultDensity;
}

export interface DensityProviderProps {
  children: ReactNode;
  defaultDensity?: Density;
}

export function DensityProvider({
  children,
  defaultDensity = "cozy",
}: DensityProviderProps) {
  const [density, setDensityState] = useState<Density>(() => readInitial(defaultDensity));

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, density);
    } catch {
      // ignore
    }
  }, [density]);

  const setDensity = useCallback((d: Density) => setDensityState(d), []);

  const value = useMemo<DensityContextValue>(() => ({ density, setDensity }), [density, setDensity]);

  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
}

export function useDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) {
    // Sinnvoller Fallback statt Throw — Komponenten ausserhalb des Providers
    // bekommen 'cozy' und einen No-Op-Setter, brechen also nicht hart.
    return { density: "cozy", setDensity: () => {} };
  }
  return ctx;
}
